import { Controller } from "stimulus"

// Form de asiento manual: agrega y saca renglones, y muestra el cuadre en vivo.
//
// Que la diferencia se vea mientras se carga es lo que evita el ida y vuelta
// con el servidor: un asiento que no cuadra no se guarda, y descubrirlo recién
// al apretar "Registrar" obliga a rehacer el conteo a mano.
export default class extends Controller {
  static targets = ["rows", "template", "debitTotal", "creditTotal", "difference", "submit",
                    "autoReverse", "autoReverseDate", "entryDate"]

  connect() {
    this.recalculate()
    if (this.hasAutoReverseTarget) this.syncAutoReverse()
  }

  // El tilde de "Reversar en el período siguiente" propone el primer día del
  // mes siguiente al asiento. Sin tilde el campo se vacía: mandar una fecha que
  // la persona ve deshabilitada dejaría reversiones programadas que nadie pidió.
  syncAutoReverse() {
    const on = this.autoReverseTarget.checked
    this.autoReverseDateTarget.disabled = !on

    if (!on) {
      this.autoReverseDateTarget.value = ""
      return
    }
    if (!this.autoReverseDateTarget.value) {
      this.autoReverseDateTarget.value = this.defaultReversalDate()
    }
  }

  // Al mover la fecha del asiento, la reversión propuesta la sigue —mientras la
  // persona no haya elegido una a mano.
  entryDateChanged() {
    if (!this.hasAutoReverseTarget || !this.autoReverseTarget.checked) return
    if (this.autoReverseDateTarget.dataset.touched === "true") return

    this.autoReverseDateTarget.value = this.defaultReversalDate()
  }

  markReversalTouched() {
    this.autoReverseDateTarget.dataset.touched = "true"
  }

  defaultReversalDate() {
    const base = this.hasEntryDateTarget && this.entryDateTarget.value
      ? new Date(`${this.entryDateTarget.value}T12:00:00`)
      : new Date()
    const next = new Date(base.getFullYear(), base.getMonth() + 1, 1, 12)
    const min = this.autoReverseDateTarget.min

    const value = next.toISOString().slice(0, 10)
    return min && value < min ? min : value
  }

  addRow(event) {
    event.preventDefault()
    const html = this.templateTarget.innerHTML.replace(/NEW_RECORD/g, new Date().getTime())
    this.rowsTarget.insertAdjacentHTML("beforeend", html)
    this.recalculate()
  }

  removeRow(event) {
    event.preventDefault()
    const row = event.target.closest("[data-journal-row]")
    const destroyField = row.querySelector("input[name*='_destroy']")

    if (destroyField) {
      // Renglón ya guardado: se marca para borrar, no se saca del DOM, o Rails
      // no se entera de que hay que eliminarlo.
      destroyField.value = "1"
      row.style.display = "none"
    } else {
      row.remove()
    }
    this.recalculate()
  }

  recalculate() {
    let debit = 0
    let credit = 0

    this.rowsTarget.querySelectorAll("[data-journal-row]").forEach((row) => {
      const destroyField = row.querySelector("input[name*='_destroy']")
      if (destroyField && destroyField.value === "1") return

      debit += this.parseAmount(row.querySelector("[data-side='debit']"))
      credit += this.parseAmount(row.querySelector("[data-side='credit']"))
    })

    const difference = debit - credit

    this.debitTotalTarget.textContent = this.format(debit)
    this.creditTotalTarget.textContent = this.format(credit)

    // Un asiento vacío no "cuadra": todavía no dice nada. Anunciar que cuadra
    // cuando no hay ni un importe cargado es peor que no decir nada.
    const empty = debit === 0 && credit === 0
    const balanced = !empty && difference === 0

    if (empty) {
      this.differenceTarget.textContent = ""
    } else if (balanced) {
      this.differenceTarget.textContent = "El asiento cuadra"
    } else {
      this.differenceTarget.textContent = `Faltan $ ${this.format(Math.abs(difference))} en el ${difference > 0 ? "haber" : "debe"}`
    }

    this.differenceTarget.classList.toggle("is-balanced", balanced)
    this.differenceTarget.classList.toggle("is-unbalanced", !empty && !balanced)

    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = difference !== 0 || debit === 0
    }
  }

  // Un renglón es debe o haber: escribir de un lado limpia el otro.
  clearOpposite(event) {
    const row = event.target.closest("[data-journal-row]")
    const side = event.target.dataset.side
    const opposite = row.querySelector(`[data-side='${side === "debit" ? "credit" : "debit"}']`)
    if (event.target.value.trim() !== "" && opposite) opposite.value = ""
    this.recalculate()
  }

  // Acepta el formato argentino ("1.234,56") además del que manda el navegador.
  parseAmount(input) {
    if (!input || !input.value) return 0
    let clean = input.value.replace(/[^\d,.-]/g, "")
    if (clean.includes(",")) {
      clean = clean.replace(/\./g, "").replace(",", ".")
    } else if (/\.\d{3}$/.test(clean)) {
      clean = clean.replace(/\./g, "")
    }
    const value = parseFloat(clean)
    return isNaN(value) ? 0 : Math.round(value * 100)
  }

  format(cents) {
    return (cents / 100).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }
}
